import {
  Building2,
  CloudUpload,
  Download,
  Eye,
  FileBarChart,
  Home,
  LayoutDashboard,
  MessageSquareText,
  Settings2,
  History,
  Trash2,
  FileSearch,
  ShieldCheck,
  WandSparkles,
  type LucideIcon
} from "lucide-react";
import type { ReactNode } from "react";
import type { CompanyContext } from "@/shared/api/api-client";
import type { Role } from "@/shared/utils/roles";
// Ponto de encaixe do Versa (módulo removível — ver versa/ARCHITECTURE.md).
import { versaNavItems, versaPageTitle } from "@/features/versa/navigation";

/**
 * `group` só existe para itens de empresa: é o subtítulo sob o qual a
 * sidebar os agrupa. Antes a lista de cada grupo era literal dentro do
 * `Sidebar`, o que fazia toda tela nova precisar ser lembrada em dois
 * lugares — e esquecer o segundo significava uma rota que existe mas não
 * aparece no menu.
 */
export type NavGroup = "monitoramento" | "recortes" | "visualizacoes";

export type NavItem = {
  label: string;
  icon: LucideIcon;
  scope: "global" | "company";
  group?: NavGroup;
  href: (companyId?: string) => string;
  /** Papel MÍNIMO pra o item aparecer — a escada de
   *  `shared/utils/roles.ts`, não o papel exato: pedir
   *  "administrador" tem que deixar o máster ver. */
  requiresRole?: Role;
  /** Só aparece se a empresa exibe CENA.
   *
   * Hoje ninguém usa: a tela de relatório DEIXOU de ser só-CENA em
   * 2026-08-10. O relatório é uma foto de menções (o CENA pegou carona
   * nela), e é dele que o dashboard tira o recorte — escondê-lo de uma
   * empresa sem CENA a deixaria sem dashboard também. Quem esconde
   * agora são os pedaços CENA dentro da própria tela (`<IfCena>`). O
   * campo fica porque o conceito continua válido pra uma tela que seja
   * inteiramente CENA. */
  requiresCena?: true;
  /** Onde o item mora. Ausente = sidebar. `account` = no menu do usuário (a
   *  Administração, desde 2026-10-15): é área da pessoa, não da navegação
   *  do dia a dia. */
  placement?: "account";
  /** O que vem logo abaixo do item no menu (ex.: os modelos do Versa
   *  fixados pela pessoa, 2026-10-30). O item continua sendo o link. */
  renderAfter?: (context: NavItemContext) => ReactNode;
};

export type NavItemContext = { companyId?: string; pathname: string; expanded: boolean; close: () => void };

/** A ordem daqui é a ordem na sidebar. O antigo grupo "RELATÓRIOS" se
 *  dividiu em dois em 2026-10-27 (pedido do Alexsander): RECORTES, com o
 *  que se gera a partir do filtro (Amostras, Downloads), e VISUALIZAÇÕES,
 *  com onde o recorte é lido (Dashboard, Versa). */
export const navigationGroups: Array<{ key: NavGroup; title: string }> = [
  { key: "monitoramento", title: "MONITORAMENTO" },
  { key: "recortes", title: "RECORTES" },
  { key: "visualizacoes", title: "VISUALIZAÇÕES" }
];

export const navigationItems: NavItem[] = [
  { label: "Início", icon: Home, scope: "global", href: () => "/" },
  { label: "Empresas", icon: Building2, scope: "global", href: () => "/empresas" },
  { label: "Visão geral", icon: Eye, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/visao-geral` },
  { label: "Análises e menções", icon: MessageSquareText, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/mencoes` },
  // O porquê de cada classificação, em cartões de leitura (2026-10-20).
  { label: "Justificativas", icon: FileSearch, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/justificativas` },
  // As menções descartadas há menos de 15 dias, com restaurar (2026-10-18).
  // Técnico ou acima: o Visualizador (2026-10-22) não descarta nem
  // restaura, e o que é "operar" some do menu dele.
  { label: "Lixeira", icon: Trash2, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/lixeira`, requiresRole: "technician" },
  // A trilha de edição de classificação da empresa inteira (2026-10-18);
  // só Administrador ou acima, o mesmo papel que a API exige.
  { label: "Histórico de edições", icon: History, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/historico-edicoes`, requiresRole: "administrator" },
  { label: "Uploads", icon: CloudUpload, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/uploads`, requiresRole: "technician" },
  // O andamento dos reprocessamentos por IA, visível de qualquer tela —
  // o mesmo papel que a API exige pra reprocessar (2026-10-14).
  { label: "Reprocessamentos", icon: WandSparkles, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/reprocessamentos`, requiresRole: "administrator" },
  { label: "Ajustes", icon: Settings2, scope: "company", group: "monitoramento", href: id => `/empresas/${id}/configuracoes` },
  { label: "Dashboard", icon: LayoutDashboard, scope: "company", group: "visualizacoes", href: id => `/empresas/${id}/dashboard` },
  { label: "Amostras", icon: FileBarChart, scope: "company", group: "recortes", href: id => `/empresas/${id}/relatorios/cena` },
  // O histórico das planilhas pedidas em fila (2026-10-15): vale pra
  // amostra e pra upload, então mora no fim do grupo de recortes.
  { label: "Downloads", icon: Download, scope: "company", group: "recortes", href: id => `/empresas/${id}/downloads`, requiresRole: "technician" },
  ...versaNavItems,
  { label: "Administração", icon: ShieldCheck, scope: "global", href: () => "/admin", requiresRole: "administrator", placement: "account" }
];

export function pageTitleForPath(pathname: string, company: CompanyContext | null, search = ""): string {
  if (pathname === "/") return "Início";
  if (pathname === "/empresas" && new URLSearchParams(search).get("nova") === "1") return "Nova empresa";
  if (pathname === "/empresas") return "Empresas";
  if (pathname === "/dashboard") return "Dashboard";
  if (pathname.startsWith("/admin")) return "Administração";
  if (pathname === "/configuracoes") return "Configurações";
  if (pathname === "/perfil") return "Meu perfil";
  if (pathname === "/preferencias") return "Preferências";
  if (pathname === "/seguranca") return "Segurança";
  if (pathname === "/ajuda") return "Central de ajuda";
  const companyLabel = company ? company.name : "Empresa";
  const versaTitle = versaPageTitle(pathname, companyLabel);
  if (versaTitle) return versaTitle;
  if (pathname.endsWith("/dashboard")) return `Dashboard · ${companyLabel}`;
  if (pathname.endsWith("/visao-geral")) return `Visão geral · ${companyLabel}`;
  if (pathname.endsWith("/mencoes")) return `Análises e menções · ${companyLabel}`;
  if (pathname.endsWith("/justificativas")) return `Justificativas · ${companyLabel}`;
  if (pathname.endsWith("/lixeira")) return `Lixeira · ${companyLabel}`;
  if (pathname.endsWith("/historico-edicoes")) return `Histórico de edições · ${companyLabel}`;
  if (pathname.endsWith("/uploads")) return `Uploads · ${companyLabel}`;
  if (pathname.endsWith("/downloads")) return `Downloads · ${companyLabel}`;
  if (pathname.includes("/reprocessamentos")) return `Reprocessamentos · ${companyLabel}`;
  if (pathname.includes("/configuracoes/assistente")) return `Assistente de configuração · ${companyLabel}`;
  if (pathname.includes("/configuracoes")) return `Ajustes · ${companyLabel}`;
  if (pathname.includes("/relatorios/cena")) {
    return `Amostras CENA de redes · ${companyLabel}`;
  }
  return "Reputaition";
}

/**
 * Para onde o "voltar" do topo leva, quando a tela é um NÍVEL ABAIXO de outra
 * (2026-10-15). Toda tela dentro de uma empresa volta pra lista de Empresas
 * (e o detalhe de um upload ou o assistente, pra tela-mãe deles). Início,
 * Empresas, a Administração e as Configurações não têm voltar.
 * O destino é sempre a tela-mãe fixa, nunca o histórico do navegador: quem
 * chega por link direto também tem pra onde voltar.
 */
export function backTargetForPath(pathname: string, search = ""): { href: string; label: string } | null {
  if (pathname === "/empresas" && new URLSearchParams(search).get("nova") === "1") return { href: "/empresas", label: "Empresas" };
  const admin = pathname.match(/^\/admin\/[^/]+/);
  if (admin) return { href: "/admin", label: "Administração" };
  const upload = pathname.match(/^(\/empresas\/[^/]+\/uploads)\/[^/]+/);
  if (upload) return { href: upload[1], label: "Uploads" };
  const settings = pathname.match(/^(\/empresas\/[^/]+\/configuracoes)\/.+/);
  if (settings) return { href: settings[1], label: "Ajustes" };
  if (/^\/empresas\/[^/]+/.test(pathname)) return { href: "/empresas", label: "Empresas" };
  return null;
}

const UPLOAD_CARRYING_ROUTE = /^\/empresas\/([^/]+)\/(mencoes|justificativas)$/;

/**
 * A query que o menu lateral acrescenta ao destino pra não perder o recorte
 * por upload ao ir de Análises e menções para Justificativas (2026-10-21).
 *
 * O filtro de upload mora na URL (`?upload=7&upload=4`), e não na loja por
 * empresa que as duas telas compartilham — é o único que o menu precisa
 * carregar. Só vale entre essas duas rotas da MESMA empresa; qualquer outro
 * destino (Lixeira, outra empresa, Uploads…) segue sem query. Só o parâmetro
 * `upload` passa: o resto da busca (`?reprocessamento=`, `?previa=`) é de
 * uma execução específica da tela de origem.
 *
 * Devolve a query com o `?`, ou `""`.
 */
export function carriedSearch(pathname: string, search: string, target: string): string {
  const from = pathname.match(UPLOAD_CARRYING_ROUTE);
  const to = target.match(UPLOAD_CARRYING_ROUTE);
  if (!from || !to || from[1] !== to[1]) return "";
  const uploads = new URLSearchParams(search).getAll("upload");
  if (uploads.length === 0) return "";
  return `?${new URLSearchParams(uploads.map<[string, string]>(id => ["upload", id])).toString()}`;
}
